/**
 * Page apprenant — /paiements
 *
 * Liste des relevés mensuels avec preview du mois courant + bouton "Réclamer".
 * Visible uniquement si `user.cash_eligible == true` (sinon redirect / message).
 *
 * Architecture défensive :
 *  - Le frontend ne stocke pas de cache critique : on refetch après chaque action
 *  - Le mois courant est volatil (preview backend) → pas de bouton claim dessus
 *  - Audit trail visible (paid_by_name, payment_reference, méthode, notes)
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    Wallet,
    Clock,
    CheckCircle2,
    AlertTriangle,
    Send,
    Sparkles,
    XCircle,
    Receipt,
    ChevronRight,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";

const STATUS_META = {
    pending: {
        label: "En attente",
        cls: "bg-white/5 text-white/65 border-white/10",
        icon: Clock,
        helper:
            "Tes points sont comptabilisés. Quand tu seras prêt, clique sur « Réclamer » pour transmettre la demande à l'admin.",
    },
    claimed: {
        label: "Demande envoyée",
        cls: "bg-shariti-yellow/15 text-shariti-yellow border-shariti-yellow/30",
        icon: Send,
        helper:
            "L'admin a été notifié. Il marquera le statut comme « payé » dès que le paiement sera effectué.",
    },
    paid: {
        label: "Payé",
        cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
        icon: CheckCircle2,
        helper: "La référence ci-dessous identifie ce paiement de manière traçable — conserve-la pour ton suivi.",
    },
    rejected: {
        label: "Rejeté",
        cls: "bg-red-500/15 text-red-300 border-red-500/30",
        icon: XCircle,
        helper:
            "Cette demande a été rejetée par l'admin. Lis le motif ci-dessous et contacte-le pour comprendre.",
    },
};

function formatEUR(n) {
    return Number(n || 0).toLocaleString("fr-FR", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
    });
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function StatusBadge({ status }) {
    const meta = STATUS_META[status] || STATUS_META.pending;
    const Icon = meta.icon;
    return (
        <span
            className={`inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full border ${meta.cls}`}
            data-testid={`payout-badge-${status}`}
        >
            <Icon className="w-3 h-3" />
            {meta.label}
        </span>
    );
}

export default function MyPayouts() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [claiming, setClaiming] = useState("");

    const reload = async () => {
        setLoading(true);
        try {
            const r = await api.get("/me/payouts");
            setData(r.data);
        } catch (e) {
            if (e.response?.status === 403) {
                setData({ blocked: true, message: e.response.data?.detail || "" });
            } else {
                toast.error(formatApiError(e.response?.data?.detail) || e.message);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!user) return;
        reload();
    }, [user]);

    const handleClaim = async (statementId, periodLabel) => {
        setClaiming(statementId);
        try {
            await api.post(`/me/payouts/${statementId}/claim`);
            toast.success(`Demande envoyée pour ${periodLabel}. L'admin a été notifié.`);
            await reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setClaiming("");
        }
    };

    return (
        <Layout>
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="my-payouts-page">
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-11 h-11 rounded-2xl bg-shariti-orange/15 flex items-center justify-center">
                        <Wallet className="w-6 h-6 text-shariti-orange" />
                    </div>
                    <div>
                        <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light">
                            Rémunération
                        </p>
                        <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                            Mes paiements
                        </h1>
                    </div>
                </div>
                <p className="text-sm text-white/55 font-light leading-relaxed mb-6 max-w-2xl">
                    Tes points gagnés ce mois-ci sont convertis en euros au taux du moment.
                    À la fin du mois, le relevé devient « réclamable » : un clic et l'admin
                    reçoit ta demande, qui sera ensuite payée.
                </p>

                {loading && (
                    <div className="glass-card rounded-2xl p-8 text-center text-white/50 text-sm">
                        Chargement…
                    </div>
                )}

                {!loading && data?.blocked && (
                    <div
                        className="glass-card rounded-2xl p-6 border-shariti-yellow/30"
                        data-testid="payouts-blocked"
                    >
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-shariti-yellow shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="font-bold text-white mb-1.5">
                                    Rémunération non activée
                                </p>
                                <p className="text-sm text-white/60 leading-relaxed">
                                    {data.message ||
                                        "Cette page sera disponible quand un administrateur t'aura activé l'éligibilité à la rémunération en €."}
                                </p>
                                <button
                                    onClick={() => navigate("/dashboard")}
                                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-shariti-orange hover:text-shariti-orange-light"
                                >
                                    Retour au dashboard
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {!loading && data && !data.blocked && (
                    <>
                        {/* Live preview of current month */}
                        {data.current_month_preview && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="rounded-2xl p-5 mb-5 bg-gradient-to-br from-shariti-orange/15 to-shariti-yellow/5 border border-shariti-orange/30"
                                data-testid="payouts-current-preview"
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <Sparkles className="w-4 h-4 text-shariti-orange" />
                                    <p className="text-[10px] font-extrabold tracking-widest uppercase text-shariti-orange-light">
                                        En cours · {data.current_month_preview.period_label}
                                    </p>
                                </div>
                                <div className="flex items-baseline gap-2 mb-1">
                                    <span className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                                        {formatEUR(data.current_month_preview.gross_amount_eur)}
                                    </span>
                                    <span className="text-sm text-white/55 font-light">
                                        ({data.current_month_preview.points_earned.toLocaleString("fr-FR")} pts)
                                    </span>
                                </div>
                                <p className="text-xs text-white/50 leading-relaxed">
                                    Estimation en temps réel — le relevé sera figé le 1er du mois prochain
                                    et deviendra réclamable.
                                </p>
                            </motion.div>
                        )}

                        {/* Past statements */}
                        <div className="space-y-3" data-testid="payouts-list">
                            {data.statements.length === 0 && (
                                <div className="glass-card rounded-2xl p-6 text-center text-white/55 text-sm">
                                    Aucun relevé mensuel pour le moment. Reviens en début de mois prochain !
                                </div>
                            )}
                            {data.statements.map((s) => {
                                const meta = STATUS_META[s.status] || STATUS_META.pending;
                                const reclaimable =
                                    s.status === "pending" && (s.gross_amount_eur || 0) > 0;
                                return (
                                    <motion.div
                                        key={s.statement_id}
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="glass-card rounded-2xl p-4 sm:p-5"
                                        data-testid={`payout-card-${s.statement_id}`}
                                    >
                                        <div className="flex flex-col sm:flex-row sm:items-start gap-3 mb-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                                    <p className="font-extrabold text-white tracking-tight">
                                                        {s.period_label}
                                                    </p>
                                                    <StatusBadge status={s.status} />
                                                </div>
                                                <div className="flex items-baseline gap-2">
                                                    <span className="text-xl font-extrabold text-shariti-orange-light">
                                                        {formatEUR(s.gross_amount_eur)}
                                                    </span>
                                                    <span className="text-xs text-white/50">
                                                        {s.points_earned.toLocaleString("fr-FR")} pts
                                                    </span>
                                                </div>
                                            </div>
                                            {reclaimable && (
                                                <button
                                                    onClick={() =>
                                                        handleClaim(s.statement_id, s.period_label)
                                                    }
                                                    disabled={claiming === s.statement_id}
                                                    data-testid={`payout-claim-${s.statement_id}`}
                                                    className="shrink-0 inline-flex items-center gap-2 bg-shariti-orange text-white font-extrabold py-2.5 px-4 rounded-xl orange-glow hover:bg-shariti-orange-light transition-all disabled:opacity-60"
                                                >
                                                    <Send className="w-4 h-4" />
                                                    {claiming === s.statement_id
                                                        ? "Envoi…"
                                                        : "Réclamer"}
                                                </button>
                                            )}
                                        </div>

                                        <p className="text-xs text-white/45 leading-relaxed mt-1">
                                            {meta.helper}
                                        </p>

                                        {/* Audit trail */}
                                        {(s.claimed_at || s.paid_at || s.rejected_reason) && (
                                            <div className="mt-3 pt-3 border-t border-white/5 space-y-1.5 text-[11px] text-white/55">
                                                {s.claimed_at && (
                                                    <div className="flex items-start gap-2">
                                                        <Send className="w-3 h-3 mt-0.5 shrink-0 text-shariti-yellow/70" />
                                                        <span>
                                                            Demande envoyée le {formatDate(s.claimed_at)}
                                                        </span>
                                                    </div>
                                                )}
                                                {s.paid_at && (
                                                    <div className="flex items-start gap-2">
                                                        <Receipt className="w-3 h-3 mt-0.5 shrink-0 text-emerald-400" />
                                                        <div className="flex-1">
                                                            <span className="text-emerald-300/85">
                                                                Payé le {formatDate(s.paid_at)}
                                                                {s.paid_by_name ? ` par ${s.paid_by_name}` : ""}
                                                            </span>
                                                            <div className="text-white/45">
                                                                {s.payment_method && (
                                                                    <>Méthode : {s.payment_method}<br /></>
                                                                )}
                                                                {s.payment_reference && (
                                                                    <>
                                                                        Référence :{" "}
                                                                        <code className="bg-black/30 px-1.5 py-0.5 rounded text-emerald-300/90 font-mono">
                                                                            {s.payment_reference}
                                                                        </code>
                                                                    </>
                                                                )}
                                                            </div>
                                                            {s.admin_notes && (
                                                                <p className="text-white/45 mt-1 italic">
                                                                    « {s.admin_notes} »
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                                {s.rejected_reason && (
                                                    <div className="flex items-start gap-2">
                                                        <XCircle className="w-3 h-3 mt-0.5 shrink-0 text-red-400" />
                                                        <span className="text-red-300/85">
                                                            Motif : {s.rejected_reason}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </motion.div>
                                );
                            })}
                        </div>

                        {data.settings && (
                            <p className="text-[11px] text-white/35 mt-6 text-center">
                                Taux de conversion en vigueur :{" "}
                                <span className="text-white/55">
                                    1 pt = {Number(data.settings.cash_rate_eur_per_point).toFixed(4)} €
                                </span>
                                {" · "}
                                Les statuts sont mis à jour par l'administrateur.
                            </p>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
}
